// frontend/src/pages/asset-admin/AssetAdminEndpointSecurity.js
/**
 * Asset Admin — Endpoint Security (Trend Micro Apex One)
 *
 * Operational surface for endpoint protection coverage + vendor licensing.
 * From an IT/Ops manager's lens it answers three recurring questions:
 *   1. Is every active endpoint protected?      → Coverage % + unprotected list
 *   2. What am I paying the vendor for?          → Licensed seats + downloadable schedule
 *   3. Which seats can I reclaim?                → Reclaimable seats + status posture
 *
 * Data: GET /asset-inventory/assets/endpoint-security-summary/
 * Report (XLSX): GET /asset-inventory/assets/endpoint-security-report/
 *
 * Theme: Red (Admin). Loading: skeleton screens. Strictly follows the
 * design-system-reference (metrics cards, section panel headers, chips, btn).
 */

import React, { useState, useMemo } from 'react';
import {
  ShieldCheck, ShieldAlert, Recycle, Laptop, Download, Loader2,
  AlertCircle, Calendar, Users, TrendingUp, Monitor, Clock,
} from 'lucide-react';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import { CountBadge } from '../../components/UI/CountBadge';
import { InfoBadge } from '../../components/UI/InfoBadge';
import {
  getDepartmentBadgeColor,
  getDepartmentShortName,
} from '../../config/departmentConfig';
import {
  useEndpointSecuritySummary,
  useDownloadEndpointSecurityReport,
} from '../../hooks/useAssetInventoryData';

// ============================================
// TONE → TAILWIND (mirrors the XLSX status chips)
// ============================================
const TONE_CHIP = {
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-600',
};

/** Map status chip tone → CountBadge theme (no purple token in CountBadge). */
const TONE_BADGE = {
  green: 'success',
  blue: 'employee',
  purple: 'neutral',
  amber: 'warning',
  red: 'admin',
  gray: 'neutral',
};

const StatusChip = ({ status, tone }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${TONE_CHIP[tone] || TONE_CHIP.gray}`}>
    {status}
  </span>
);

/** Dense table/card department chip — DS Department Identity System. */
const DepartmentChip = ({ department }) => {
  if (!department) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <InfoBadge
      color={getDepartmentBadgeColor(department)}
      label={getDepartmentShortName(department)}
      title={department}
      size="xs"
    />
  );
};

// ============================================
// METRIC CARD (DS metrics-card pattern)
// ============================================
const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SKELETON
// ============================================
const PageSkeleton = () => (
  <div className="space-y-6 bg-gray-50 min-h-screen">
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
      <div className="flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded w-48" />
            <div className="h-4 bg-gray-200 rounded w-64" />
          </div>
        </div>
        <div className="h-10 bg-gray-200 rounded-full w-40 hidden sm:block" />
      </div>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="ml-2 sm:ml-4 space-y-2">
              <div className="h-8 bg-gray-200 rounded w-16" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
      <div className="space-y-2">
        {[1, 2, 3, 4].map((j) => <div key={j} className="h-10 bg-gray-100 rounded-lg" />)}
      </div>
    </div>
  </div>
);

// ============================================
// COVERAGE BAR
// ============================================
const coverageTone = (pct) => {
  if (pct >= 90) return { bar: 'bg-green-500', text: 'text-green-600', label: 'Healthy' };
  if (pct >= 75) return { bar: 'bg-amber-500', text: 'text-amber-600', label: 'Needs attention' };
  return { bar: 'bg-red-500', text: 'text-red-600', label: 'At risk' };
};

// ============================================
// MAIN
// ============================================
const AssetAdminEndpointSecurity = () => {
  const { data: summary, isLoading, error } = useEndpointSecuritySummary();
  const downloadReport = useDownloadEndpointSecurityReport();

  const [registerSearch, setRegisterSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const register = summary?.register || [];
  const filteredRegister = useMemo(() => {
    const q = registerSearch.trim().toLowerCase();
    return register.filter((ep) => {
      if (statusFilter !== 'all' && ep.status !== statusFilter) return false;
      if (!q) return true;
      return [ep.asset_tag, ep.user, ep.staff_id, ep.department, ep.endpoint, ep.device]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [register, registerSearch, statusFilter]);

  if (isLoading) return <PageSkeleton />;

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load endpoint security data</p>
          <p className="text-red-600 text-sm mt-1">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  const kpis = summary?.kpis || {};
  const period = summary?.period || {};
  const statusBreakdown = summary?.status_breakdown || [];
  const monthly = summary?.monthly || [];
  const unprotected = summary?.unprotected || [];
  const coverage = Number(kpis.coverage_pct || 0);
  const cov = coverageTone(coverage);

  const handleDownload = () => {
    if (downloadReport.isPending) return;
    downloadReport.mutate();
  };

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Endpoint Security</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
                Trend Micro Apex One coverage &amp; vendor licensing
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloadReport.isPending}
              aria-label="Licensing Report"
              title="Licensing Report"
              className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex items-center gap-2"
            >
              {downloadReport.isPending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">Licensing Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Coverage"
          value={`${coverage}%`}
          subtitle={`${kpis.protected_active || 0} of ${kpis.eligible_active || 0} active`}
          icon={ShieldCheck}
          color={coverage >= 90 ? 'green' : coverage >= 75 ? 'amber' : 'red'}
        />
        <MetricCard
          title="Licensed Seats"
          value={kpis.licensed_seats || 0}
          subtitle="Paid Apex One agents"
          icon={Laptop}
          color="blue"
        />
        <MetricCard
          title="Unprotected"
          value={kpis.unprotected_active || 0}
          subtitle="Active endpoints, no agent"
          icon={ShieldAlert}
          color="red"
        />
        <MetricCard
          title="Reclaimable"
          value={kpis.reclaimable || 0}
          subtitle="Seats not in active use"
          icon={Recycle}
          color="amber"
        />
      </div>

      {/* Coverage bar */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <TrendingUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 truncate">Protection Coverage</h3>
          </div>
          <span className={`text-sm font-semibold ${cov.text}`}>{cov.label}</span>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${cov.bar} rounded-full transition-all`} style={{ width: `${Math.min(coverage, 100)}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>{kpis.protected_active || 0} protected</span>
          <span>{kpis.unprotected_active || 0} unprotected · {kpis.eligible_active || 0} active endpoints</span>
        </div>
      </div>

      {/* Status posture + Monthly schedule */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seat status posture */}
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Seat Status &amp; Licensing</h3>
              <span className="text-sm text-gray-500">{kpis.licensed_seats || 0} seats</span>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {statusBreakdown.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No licensed seats yet.</p>
            ) : statusBreakdown.map((row) => (
              <div key={row.status} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3 min-w-0">
                  <StatusChip status={row.status} tone={row.tone} />
                  <span className="text-xs text-gray-500 truncate hidden sm:inline">{row.implication}</span>
                </div>
                <span className="text-sm font-bold text-gray-900 flex-shrink-0">{row.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly install schedule */}
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Install Schedule</h3>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {period.earliest} → {period.latest}
              </span>
            </div>
          </div>
          <div className="p-4">
            {monthly.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No installations recorded.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {monthly.map((m) => (
                  <div key={m.month} className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-gray-50">
                    <span className="text-sm text-gray-700 truncate">{m.month}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        +{m.installed}
                      </span>
                      <span className="text-xs text-gray-400 w-16 text-right">{m.cumulative} total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Licensed endpoints — the full register (mirrors the XLSX) */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Laptop className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 min-w-0">
                <span className="truncate">Licensed Endpoints</span>
                <CountBadge
                  count={filteredRegister.length}
                  theme="admin"
                  size="md"
                  variant="solid"
                  max={9999}
                  className="flex-shrink-0"
                />
                {filteredRegister.length !== register.length && (
                  <span className="text-sm font-normal text-gray-500 flex-shrink-0 whitespace-nowrap">
                    of {register.length}
                  </span>
                )}
              </h3>
            </div>
            <DebouncedSearchInput
              value={registerSearch}
              onSearch={setRegisterSearch}
              placeholder="Search tag, user, department, host…"
              theme="admin"
              className="sm:max-w-xs"
            />
          </div>
          {/* Status filter chips — data-driven: single-row scroll, CountBadge (no parentheses) */}
          {statusBreakdown.length > 0 && (
            <div className="mt-3 flex items-center gap-2 overflow-x-auto scroll-smooth">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${
                  statusFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
                <CountBadge
                  count={register.length}
                  theme="neutral"
                  active={statusFilter === 'all'}
                  variant="solid"
                  max={9999}
                  className="flex-shrink-0"
                />
              </button>
              {statusBreakdown.map((row) => {
                const active = statusFilter === row.status;
                return (
                  <button
                    key={row.status}
                    type="button"
                    onClick={() => setStatusFilter(active ? 'all' : row.status)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${TONE_CHIP[row.tone] || TONE_CHIP.gray} ${
                      active ? 'ring-2 ring-offset-1 ring-gray-500' : 'opacity-80 hover:opacity-100'
                    }`}
                  >
                    {row.status}
                    <CountBadge
                      count={row.count}
                      theme={TONE_BADGE[row.tone] || 'neutral'}
                      active={active}
                      variant="solid"
                      max={9999}
                      className="flex-shrink-0"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {filteredRegister.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No endpoints match your search.</p>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="p-4 space-y-3 md:hidden">
              {filteredRegister.map((ep) => (
                <div key={ep.asset_tag} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{ep.asset_tag}</p>
                      <p className="text-xs text-gray-500 truncate">{ep.device}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusChip status={ep.status} tone={ep.tone} />
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-500">
                    <p className="flex items-center gap-1 truncate">
                      <Users className="w-3 h-3 flex-shrink-0" />
                      {ep.user ? `${ep.user}${ep.staff_id ? ` · ${ep.staff_id}` : ''}` : 'Unassigned'}
                    </p>
                    {ep.department && (
                      <div className="pt-0.5">
                        <DepartmentChip department={ep.department} />
                      </div>
                    )}
                    <p className="flex items-center gap-1 truncate"><Monitor className="w-3 h-3" /> {ep.endpoint}</p>
                    <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> Installed {ep.install_label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Asset Tag</th>
                    <th className="px-4 py-3">Device</th>
                    <th className="px-4 py-3">Current User</th>
                    <th className="px-4 py-3">Department</th>
                    <th className="px-4 py-3">Endpoint Host</th>
                    <th className="px-4 py-3 whitespace-nowrap">Installed</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRegister.map((ep) => (
                    <tr key={ep.asset_tag} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{ep.asset_tag}</td>
                      <td className="px-4 py-3 text-gray-600">{ep.device}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {ep.user
                          ? <span>{ep.user}{ep.staff_id && <span className="text-gray-400"> · {ep.staff_id}</span>}</span>
                          : <span className="text-gray-400 italic">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3">
                        <DepartmentChip department={ep.department} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{ep.endpoint}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ep.install_label}</td>
                      <td className="px-4 py-3"><StatusChip status={ep.status} tone={ep.tone} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Unprotected endpoints — the security gap */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 min-w-0">
              <span className="truncate">Unprotected Endpoints</span>
              <CountBadge
                count={unprotected.length}
                theme="admin"
                size="md"
                variant="solid"
                max={9999}
                className="flex-shrink-0"
              />
            </h3>
          </div>
        </div>

        {unprotected.length === 0 ? (
          <div className="p-8 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <ShieldCheck className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-medium text-gray-900">Every active endpoint is protected</h4>
            <p className="text-sm text-gray-500 mt-1">All eligible laptops &amp; desktops carry the Apex One agent.</p>
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="p-4 space-y-3 md:hidden">
              {unprotected.map((ep) => (
                <div key={ep.asset_tag} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{ep.asset_tag}</p>
                      <p className="text-xs text-gray-500 truncate">{ep.device}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusChip status={ep.status} tone={ep.tone} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1 min-w-0 truncate">
                      <Users className="w-3 h-3 flex-shrink-0" />
                      {ep.user ? `${ep.user}${ep.staff_id ? ` · ${ep.staff_id}` : ''}` : 'Unassigned'}
                    </span>
                  </div>
                  {ep.department && (
                    <div className="mt-1.5">
                      <DepartmentChip department={ep.department} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Asset Tag</th>
                    <th className="px-4 py-3">Device</th>
                    <th className="px-4 py-3">Current User</th>
                    <th className="px-4 py-3">Department</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unprotected.map((ep) => (
                    <tr key={ep.asset_tag} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-900">{ep.asset_tag}</td>
                      <td className="px-4 py-3 text-gray-600">{ep.device}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {ep.user
                          ? <span>{ep.user}{ep.staff_id && <span className="text-gray-400"> · {ep.staff_id}</span>}</span>
                          : <span className="text-gray-400 italic">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3">
                        <DepartmentChip department={ep.department} />
                      </td>
                      <td className="px-4 py-3"><StatusChip status={ep.status} tone={ep.tone} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssetAdminEndpointSecurity;
